/**
 * `/api/coffee` — the `coffee` module: the company's coffee machines, read
 * through the manufacturers' clouds, and woken from standby.
 *
 * **Accounts** are credentials for a manufacturer's cloud (a provider: De'Longhi
 * today). Each account brings the machines registered to it. Managing them
 * needs `coffee.manage`; the password is sealed on the server and never sent
 * back.
 *
 * **Machines**, their status and their counters need `coffee.use`, and so do
 * waking one and putting it to sleep. Every user with it sees every machine.
 */
/**
 * The module's permissions. Values, not types: the backend's manifest
 * registers them and the frontend gates on the same strings.
 */
export const COFFEE_PERMISSIONS = {
    /** See every machine, its status and counters; wake one, or put it to sleep. */
    USE: 'coffee.use',
    /** Add, change, sync and remove the cloud accounts the machines come from. */
    MANAGE: 'coffee.manage',
};
/** Bounds the server enforces, exported so a form can show them first. */
export const COFFEE_LIMITS = {
    /** Accounts in all. */
    accounts: 50,
    /** Machines in all; an account that brings more keeps the first by the provider's order. */
    machines: 200,
    labelLength: 100,
    /** Any one credential field. */
    credentialLength: 500,
};
/**
 * What a machine is doing, the same for every provider.
 *
 * - `ready` — on and ready to brew.
 * - `busy` — on and doing something: brewing, rinsing, heating, descaling.
 * - `waking` — starting up after a wake.
 * - `standby` — asleep; a wake turns it on.
 * - `offline` — the provider's cloud cannot reach the machine.
 * - `unknown` — the cloud reaches it, but its last report is too old or
 *   unreadable to say. A wake is still offered.
 */
export const COFFEE_MACHINE_STATES = ['ready', 'busy', 'waking', 'standby', 'offline', 'unknown'];
/**
 * Conditions a machine reports, the same for every provider. A provider maps
 * what it can read onto these and leaves out what it cannot.
 */
export const COFFEE_ALARMS = [
    'water_tank_empty',
    'grounds_container_full',
    'descale_needed',
    'replace_water_filter',
    'ground_too_fine',
    'beans_empty',
    'service_required',
    'heater_probe_failure',
    'too_much_coffee',
    'infuser_motor_failure',
    'steamer_probe_failure',
    'drip_tray_missing',
    'hydraulic_problem',
    'clean_milk_knob',
    'cleaning_needed',
    'bean_hopper_missing',
];
/**
 * Drinks a provider can count, the same for every provider: also the
 * translation keys, `coffee:drink.<key>`. A provider counts what it can and
 * leaves out the rest.
 */
export const COFFEE_DRINKS = [
    'espresso',
    'coffee',
    'long_coffee',
    'doppio',
    'americano',
    'long_black',
    'cappuccino',
    'cappuccino_doppio',
    'cappuccino_reverse',
    'latte_macchiato',
    'caffe_latte',
    'flat_white',
    'espresso_macchiato',
    'cortado',
    'hot_milk',
    'hot_water',
    'tea',
    'coffee_pot',
    'brew_over_ice',
    'travel_mug',
    'iced',
    'cold_brew',
];
//# sourceMappingURL=coffee.js.map